import { registerBrick, Transaction, Query, BrickContext, CloudObject, PropertyModel, Brick, QueryResult} from 'olympe';
import { getLogger, ReactBrick, useProperty, useMUITheme } from '@olympeio/core'
import React, {useEffect, useState, useCallback} from 'react';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarExport, GridFooter, GridFooterContainer} from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { getPropertyValue, filterByValue, getPropertyType } from './helper';
import { getIcon } from '@olympeio-extensions/commons';
import { format} from "date-fns";

export default class EditableDatagrid extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
            $.observe('Data Type', false),
            $.observe('Editable', false).pipe(map(editable => editable ?? true)),
            $.observe('Hide Tags', false).pipe(map(hideTags => hideTags ?? true)),
        ]);
    }

    static handleError($, message) {
        getLogger('Dataset').error(message);
        const onError = $.get('On Error');
        if (onError){
            const [startInput, messageInput] = onError.getInputs();
            $.get('On Error Context').set(messageInput, message).trigger(startInput);
        }
    }

    /**
     * @override
     */
    static getReactComponent($) {
        const onError = $.get('On Error');
        if (onError instanceof Brick) {
            $.set('On Error Context', $.runner(onError));
        }

        const logger = getLogger('Dataset Editor');
        const onSelectedProps = $.get('On Selected');
        const onSelected = onSelectedProps instanceof Brick ? [$.runner(onSelectedProps), ...onSelectedProps.getInputs()] : null;

        return (props) => {
            const [hidden, dataType, editable] = props.values;

            if (!(dataType instanceof CloudObject)) {
                logger.error('Please set the Data Type property for proper display of the datatable');
            }

            const refresh = useProperty($, 'Refresh');
            const [rows, setRows] = useState([]);
            const propertyColumns = EditableDatagrid.getColumnsFromProps($, dataType, editable);

            const onRefresh = () => {
                EditableDatagrid.fetchData($, dataType).then((result) => {
                    setRows(result.map((entry, index) => EditableDatagrid.processRow(index, entry, propertyColumns)));
                }).catch((e) => logger.error(`Error occurred while fetching data: ${e}`));
            };

            const onAdd = () => {
                if (dataType instanceof CloudObject) {
                    EditableDatagrid.createObject($, dataType).then(() => onRefresh());
                }
            };

            const onDelete = (tag) => {
                const transaction = new Transaction(true);
                transaction.delete(tag);
                Transaction.process($, transaction).catch((message) => {
                    EditableDatagrid.handleError($, `An error were encountered while deleting a CloudObject: ${message}`);
                }).then(() => onRefresh());
            };

            // Column with the Add button at the top and delete for each line.
            const actionColumn = EditableDatagrid.getActionColumn($, onAdd, onDelete);
            const columns = [...propertyColumns, actionColumn];

            // Call refresh for the initial fetch of data and every time "refresh" event is called.
            useEffect(() => onRefresh(), [refresh]);

            return !hidden && (
                <div style={{ height: '100%', width: '100%' }}>
                    <EditableDatagrid.MuiDataGrid
                        $={$}
                        columns={columns}
                        rows={rows}
                        onRefresh={onRefresh}
                        onSelected={onSelected}
                    />
                </div>
            );
        }
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {CloudObject} dataType
     * @return {!Promise<QueryResult>}
     */
    static fetchData($, dataType) {
        return dataType instanceof CloudObject ? Query.instancesOf(dataType).execute() : Promise.resolve(QueryResult.empty());
    }

    /**
     * Return the action column header and cell renderer.
     *
     * @private
     * @param {!BrickContext} $
     * @param {function()} onAdd
     * @param {function(string)} onDelete
     * @return {!Object}
     */
    static getActionColumn($, onAdd, onDelete) {
        return {
            field: 'Remove Object',
            sortable: false,
            editable: false,
            disableColumnMenu: true,
            renderHeader: () => (useProperty($, 'Hide Create Button') === false &&
                <IconButton
                    aria-label="add"
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginLeft: 16 }}
                    onClick={onAdd}
                >{getIcon('add')}</IconButton>
            ),
            renderCell: (params) => (
                <IconButton
                    aria-label="delete"
                    variant="contained"
                    color="error"
                    size="small"
                    style={{ marginLeft: 16 }}
                    onClick={() => onDelete(params.row.entry)}
                >{getIcon('delete')}</IconButton>
            ),
        };
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {!CloudObject} dataType
     * @param {boolean} editable
     * @return {!Array<!Object>}
     */
    static getColumnsFromProps($, dataType, editable) {
        if (!(dataType instanceof CloudObject)) {
            return [];
        }

        const columnsWidth = useProperty($, 'Columns Width');

        const columns = dataType.follow(CloudObject.propertyRel).executeFromCache().map((property, index) => {
            const type = property.followSingle(PropertyModel.typeRel).executeFromCache();
            const name = property.name();
            return {
                property: property, // Olympe datamodel info
                type: getPropertyType(type, name), // React props
                editable: editable,
                field: name,
                headerName: name,
                flex: Number(columnsWidth?.split(';')[index] ?? 1),
            };
        });

        if (!$.get('Hide Tags')) {
            const tagColumn = {
                field: 'Tag',
                header: 'ID',
                editable: false,
            };
            columns.unshift(tagColumn);
        }
        return columns;
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {!CloudObject} dataType
     * @return {!Promise<void>}
     */
    static createObject($, dataType) {
        const transaction = new Transaction(true);
        transaction.create(dataType);

        return transaction.execute().catch((msg) => {
            getLogger('Dataset').error(`An error were encountered while creating a new CloudObject: ${msg}`);
        });
    }

    /**
     * @private
     * @param {number} index
     * @param {CloudObject} entry
     * @param {!Array<!Object>} columns
     * @return {!Object}
     */
    static processRow(index, entry, columns) {
        const rowObject = {
            id: index,
            filterable: true,
            entry: entry,
            tag: entry.getTag(), // used for the selection and updates
        };

        // Append properties values, in the order defined
        columns.forEach((col) => {
            rowObject[col.field] = getPropertyValue(entry, col);
        });

        return rowObject;
    }
}

/**
 * @param {!BrickContext} $
 * @param {!Array<!Object>} rows
 * @param {!Array<!Object>} columns
 * @param {function()} onRefresh
 * @param {function()} onSelected
 */
EditableDatagrid.MuiDataGrid = ({ $, rows, columns, onRefresh, onSelected }) => {
    const rowHeight = 40;
    const [currentColumns, setCurrentColumns] = useState(columns);
    const [filteredRows, setFilteredRows] = useState(rows);
    const height = useProperty($, 'Height');
    const width = useProperty($, 'Width');
    const dateFormat = useProperty($, 'Date Format');

    // Refresh column width and height on component height or width change
    React.useEffect(() => {

        const cols = columns.map(column => {
            return {
                ...column,
                valueFormatter: (params) => {
                    const newValue = getFormattedDate(column, params, dateFormat);
                    const defaultValues = { 'number': 0, 'string': '', 'boolean': false };
                    const isNull = params.value?.toString() === 'null';
                    return isNull ? defaultValues[column.type] : newValue;
                }
            };
        });
        setCurrentColumns(cols);
    }, [height, width, dateFormat]);

    React.useEffect(() => {
        setFilteredRows(rows);
    }, [rows]);

    /**
     * @param {string} value
     */
    const search = (value) => {
        if (value !== '') {
            setFilteredRows(filterByValue(rows, value));
        } else {
            setFilteredRows(rows);
        }
    };

    /**
     * @param colObj
     * @param params
     * @param dateFormat
     * @return {string|*}
     */
    const getFormattedDate = (colObj, params, dateFormat) => {
        const isValidDataType = colObj.type === 'dateTime' && params.value instanceof Date && !isNaN(params.value) && dateFormat;
        if (isValidDataType) {
            try {
                return format(params.value, dateFormat);
            } catch (e) {
                getLogger('Dataset Editor').error('Incorrect Date Format: ' + e.message);
            }
        }
        return params.value;
    };

    /**
     * Selection: conditionally fire event On Selected
     * @param {!Object|Object[]} newSelection
     */
    const onSelectionModelChange = (newSelection) => {
        // Get the index of the last selected row
        const lastIndex = newSelection[newSelection.length - 1];
        const [lastSelectedRow] = filteredRows.filter(row => row.id === lastIndex);

        if (lastSelectedRow !== undefined && onSelected) {
            const [onClick$, ctrlflowInput, indexInput, objectInput] = onSelected;
            onClick$
                .set(indexInput, lastSelectedRow.id)
                .set(objectInput, lastSelectedRow.tag)
                .trigger(ctrlflowInput);
        }
    };

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>
        );
    };

    const CustomFooter = () => {
        return (
            <GridFooterContainer>
                <IconButton
                    aria-label="refresh"
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ paddingLeft: 10 }}
                    onClick={onRefresh}
                >{getIcon('refresh')}</IconButton>
                <GridFooter />
            </GridFooterContainer>
        );
    };

    // Execute a transaction each time a row is updated.
    const processRowUpdate = useCallback((newRow, oldRow) => {
        const transaction = new Transaction();
        const instance = newRow.entry;
        // Find the properties that have been updated and add this operation to the transaction.
        for (const {field, property} of currentColumns) {
            const newValue = newRow[field];
            if (newValue !== oldRow[field]) {
                transaction.update(instance, property, newValue);
            }
        }
        // Execute the transaction then return the newRow without as is to update the data-grid state.
        return transaction.execute().then(() => newRow);
    }, [currentColumns]);

    // Handle error happening when procesing a row update event (eg: transaction refused).
    const processRowUpdateError = useCallback((e) => {
        EditableDatagrid.handleError($, `An error were encountered while updating a row: ${e}`);
    }, []);
    const hideToolbar = useProperty($,'Hide Toolbar')
    const myComponents = {Footer: CustomFooter}
    if(!hideToolbar) { // bug here
       myComponents.Toolbar = CustomToolbar;
    }


    const theme = useMUITheme($);
    return (
        <ThemeProvider theme={theme}>
            <div style={{position: 'absolute', top: '45px', bottom: '0px', width: '100%'}}>
                <DataGrid
                    rows={filteredRows}
                    columns={currentColumns}
                    rowHeight={rowHeight}
                    autoPageSize={true}
                    components={myComponents}
                    experimentalFeatures={{ newEditingApi: true }}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={processRowUpdateError}
                    onSelectionModelChange={onSelectionModelChange}
                />
            </div>
            <EditableDatagrid.Search search={search} />
        </ThemeProvider>
    );
};

/**
 * @param {boolean} isShown
 * @param {function(string)} search
 */
EditableDatagrid.Search = ({ search }) => {
    const language = localStorage.getItem('current-language');
    return (
        <TextField
            id="outlined-basic"
            variant="outlined"
            placeholder={language === 'fr' ? 'Rechercher...' : 'search...'}
            size="small"
            style={{
                height: '40px', width: '200px',
                position: 'absolute', right: '0px', top: '0px',
                display: 'block',
            }}
            onChange={(event) => search(event.target.value)}
        />
    );
};

registerBrick('018e998213a019649b29', EditableDatagrid);